import { ExportOutlined, FilePdfOutlined, PrinterOutlined } from '@ant-design/icons';
import {
  Alert,
  App,
  Button,
  DatePicker,
  Empty,
  Flex,
  InputNumber,
  Pagination,
  Select,
  Space,
  Spin,
  Typography,
} from 'antd';
import dayjs from 'dayjs';
import { AdminContext } from 'constants/contexts';
import { OrderStatusLabel } from 'constants/status';
import useFetchedData from 'hooks/useFetchedData';
import React, {
  useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import { flushSync } from 'react-dom';
import { Link, useSearchParams } from 'react-router-dom';
import http, { getHttpErrorMessage } from 'utils/http';
import PrepSheetOrderCard from './PrepSheetOrderCard';
import { downloadResponseBlob, getBlobHttpErrorMessage } from './kitchenSheetDownload';
import { buildPrepSheetCsvFilename, buildPrepSheetPdfFilename } from './prepSheetExport';
import useCateringRestaurants from './useCateringRestaurants';
import usePrepSheetChecklist from './usePrepSheetChecklist';
import {
  disablePrintCapture,
  enablePrintCapture,
  prepareBrowserPrint,
  restoreBrowserPrint,
} from './prepBrowserPrint';
import {
  applyPrepSheetPrintPageBreaks,
  clearPrepSheetPrintPageBreaks,
  waitForNextPaint,
} from './prepSheetPrintBreaks';
import './prep-sheet.css';

const { RangePicker } = DatePicker;

const STATUS_OPTIONS = Object.entries(OrderStatusLabel).map(([statusId, label]) => ({
  value: Number(statusId),
  label,
}));

const PREP_SHEET_PAGE_SIZE = 10;
const AUTO_COLLAPSE_ORDER_THRESHOLD = 3;

function formatDateRange(dateRange) {
  if (!dateRange?.[0] || !dateRange?.[1]) {
    return 'All dates';
  }
  const start = dateRange[0].format('MMM D, YYYY');
  const end = dateRange[1].format('MMM D, YYYY');
  return start === end ? start : `${start} – ${end}`;
}

function PrepSheet() {
  const { message } = App.useApp();
  const { organizationId } = useContext(AdminContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const scopedOrderId = searchParams.get('order_id');
  const [dateRange, setDateRange] = useState(() => (
    scopedOrderId ? null : [dayjs(), dayjs()]
  ));
  const [restaurantIds, setRestaurantIds] = useState([]);
  const [statusIds, setStatusIds] = useState([]);
  const [headcountMin, setHeadcountMin] = useState(null);
  const [headcountMax, setHeadcountMax] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [page, setPage] = useState(1);
  const [printOrders, setPrintOrders] = useState(null);
  const [printLoading, setPrintLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [expandedOrderIds, setExpandedOrderIds] = useState(() => new Set());
  const prepSheetRootRef = useRef(null);

  useEffect(() => {
    if (scopedOrderId) {
      setDateRange(null);
      setRestaurantIds([]);
      setStatusIds([]);
      setHeadcountMin(null);
      setHeadcountMax(null);
      setPage(1);
    }
  }, [scopedOrderId]);

  const filterQuery = useMemo(() => ({
    order_id: scopedOrderId || undefined,
    start_date: (!scopedOrderId && dateRange?.[0]) ? dateRange[0].format('YYYY-MM-DD') : undefined,
    end_date: (!scopedOrderId && dateRange?.[1]) ? dateRange[1].format('YYYY-MM-DD') : undefined,
    restaurant_ids: restaurantIds.length ? restaurantIds.join(',') : undefined,
    status: statusIds.length ? statusIds.join(',') : undefined,
    headcount_min: headcountMin != null && headcountMin >= 1 ? headcountMin : undefined,
    headcount_max: headcountMax != null && headcountMax >= 1 ? headcountMax : undefined,
    tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
  }), [scopedOrderId, dateRange, restaurantIds, statusIds, headcountMin, headcountMax]);

  const listQuery = useMemo(() => ({
    ...filterQuery,
    page,
    page_size: PREP_SHEET_PAGE_SIZE,
  }), [filterQuery, page]);

  const filterError = useMemo(() => {
    if (dateRange?.[0] && dateRange?.[1] && dateRange[0].isAfter(dateRange[1], 'day')) {
      return 'Start date must be on or before the end date.';
    }
    if (
      (headcountMin != null && headcountMin < 1)
      || (headcountMax != null && headcountMax < 1)
    ) {
      return 'Headcount must be at least 1 when set.';
    }
    if (
      headcountMin != null
      && headcountMax != null
      && headcountMin > headcountMax
    ) {
      return 'Headcount minimum cannot be greater than the maximum.';
    }
    return null;
  }, [dateRange, headcountMin, headcountMax]);

  const prepSheetParams = organizationId && !filterError ? {
    url: `v1/organizations/${organizationId}/prep-sheet/`,
    query: listQuery,
  } : null;

  const {
    data: prepSheetData,
    loading: prepSheetLoading,
    error: prepSheetError,
  } = useFetchedData(
    prepSheetParams,
    (response = {}) => ({
      orders: response?.data?.results || response?.results || response?.data || [],
      totalCount: response?.data?.count ?? response?.count ?? 0,
    }),
  );

  const { restaurantOptions } = useCateringRestaurants();
  const { isChecked, setChecked, getProgress } = usePrepSheetChecklist(organizationId);
  const orders = filterError ? [] : (prepSheetData?.orders || []);
  const displayOrders = printOrders ?? orders;
  const totalCount = filterError ? 0 : (prepSheetData?.totalCount ?? orders.length);
  const dateRangeLabel = formatDateRange(dateRange);
  useEffect(() => {
    setPage(1);
  }, [dateRange, restaurantIds, statusIds, headcountMin, headcountMax, scopedOrderId]);

  useEffect(() => {
    if (orders.length === 0) {
      setExpandedOrderIds(new Set());
      return;
    }
    if (scopedOrderId) {
      setExpandedOrderIds(new Set(orders.map((order) => order.order_id)));
      return;
    }
    if (orders.length <= AUTO_COLLAPSE_ORDER_THRESHOLD) {
      setExpandedOrderIds(new Set(orders.map((order) => order.order_id)));
      return;
    }
    setExpandedOrderIds(new Set([orders[0].order_id]));
  }, [orders, page, scopedOrderId]);

  const toggleOrderExpanded = (orderId) => {
    setExpandedOrderIds((current) => {
      const next = new Set(current);
      if (next.has(orderId)) {
        next.delete(orderId);
      } else {
        next.add(orderId);
      }
      return next;
    });
  };

  const totalItems = useMemo(
    () => displayOrders.reduce(
      (sum, order) => sum + (order.items || []).reduce(
        (itemSum, item) => itemSum + (Number(item.quantity) || 0),
        0,
      ),
      0,
    ),
    [displayOrders],
  );

  const applyPrintPageBreaks = useCallback(() => {
    applyPrepSheetPrintPageBreaks(prepSheetRootRef.current);
  }, []);

  const resetPrintLayout = useCallback(() => {
    disablePrintCapture(prepSheetRootRef.current);
    clearPrepSheetPrintPageBreaks(prepSheetRootRef.current);
  }, []);

  useEffect(() => {
    const onBeforePrint = () => {
      prepareBrowserPrint();
      enablePrintCapture(prepSheetRootRef.current);
      applyPrintPageBreaks();
    };
    const onAfterPrint = () => {
      setPrintOrders(null);
      resetPrintLayout();
      restoreBrowserPrint();
    };
    window.addEventListener('beforeprint', onBeforePrint);
    window.addEventListener('afterprint', onAfterPrint);
    return () => {
      window.removeEventListener('beforeprint', onBeforePrint);
      window.removeEventListener('afterprint', onAfterPrint);
      restoreBrowserPrint();
      disablePrintCapture(prepSheetRootRef.current);
    };
  }, [applyPrintPageBreaks, resetPrintLayout]);

  const fetchAllOrdersForPrint = useCallback(async () => {
    const pageSize = 100;
    let pageNum = 1;
    let allOrders = [];
    let total = 0;

    while (true) {
      const response = await http.get(`v1/organizations/${organizationId}/prep-sheet/`, {
        params: {
          ...filterQuery,
          page: pageNum,
          page_size: pageSize,
        },
      });
      const data = response?.data?.data || response?.data || {};
      const batch = data?.results ?? data?.orders ?? (Array.isArray(data) ? data : []);
      total = data?.count ?? totalCount ?? batch.length;
      allOrders = allOrders.concat(batch);
      if (allOrders.length >= total || batch.length < pageSize) {
        break;
      }
      pageNum += 1;
    }
    return allOrders;
  }, [organizationId, filterQuery, totalCount]);

  const preparePrintLayout = useCallback(async () => {
    const allOrders = await fetchAllOrdersForPrint();
    flushSync(() => {
      setPrintOrders(allOrders);
      setExpandedOrderIds(new Set(allOrders.map((order) => order.order_id)));
    });
    await waitForNextPaint();
  }, [fetchAllOrdersForPrint]);

  const handlePrint = async () => {
    if (filterError) {
      message.warning(filterError);
      return;
    }
    if (!totalCount) {
      message.warning('No orders to print for the current filters.');
      return;
    }
    setPrintLoading(true);
    try {
      await preparePrintLayout();
      window.print();
    } catch (error) {
      message.error(getHttpErrorMessage(error, false) || 'Unable to prepare prep sheet for print');
    } finally {
      setPrintLoading(false);
    }
  };

  const onExportPdf = async () => {
    if (filterError) {
      message.warning(filterError);
      return;
    }
    setPdfLoading(true);
    try {
      const response = await http.get(`v1/organizations/${organizationId}/prep-sheet/`, {
        params: {
          ...filterQuery,
          export: 'pdf',
        },
        responseType: 'blob',
      });
      downloadResponseBlob(response, buildPrepSheetPdfFilename(dateRange));
    } catch (error) {
      const errorMessage = await getBlobHttpErrorMessage(error);
      message.error(errorMessage || 'Unable to export prep sheet PDF');
    } finally {
      setPdfLoading(false);
    }
  };

  const onExportCsv = async () => {
    if (filterError) {
      message.warning(filterError);
      return;
    }
    setExporting(true);
    try {
      const response = await http.get(`v1/organizations/${organizationId}/prep-sheet/`, {
        params: {
          ...filterQuery,
          export: 'csv',
        },
        responseType: 'blob',
      });
      downloadResponseBlob(response, buildPrepSheetCsvFilename(dateRange));
    } catch (error) {
      const errorMessage = await getBlobHttpErrorMessage(error);
      message.error(errorMessage || 'Unable to export prep sheet');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="prep-sheet-root" ref={prepSheetRootRef}>
      <header className="prep-sheet-print-banner prep-sheet-print-only">
        <Typography.Title level={3} className="prep-sheet-print-title">
          Prep sheet
        </Typography.Title>
        <Typography.Text className="prep-sheet-print-line">{dateRangeLabel}</Typography.Text>
        <Typography.Text className="prep-sheet-print-line">
          {totalCount}
          {' '}
          {totalCount === 1 ? 'order' : 'orders'}
        </Typography.Text>
        <Typography.Text type="secondary" className="prep-sheet-print-line">
          Generated
          {' '}
          {dayjs().format('MMM D, YYYY [at] h:mm A')}
        </Typography.Text>
      </header>

      <div className="prep-sheet-page-header prep-sheet-no-print">
        <nav className="prep-sheet-breadcrumb" aria-label="Breadcrumb">
          <Link to="/console/prep-kitchen/ingredients">Prep &amp; kitchen</Link>
          <span className="prep-sheet-breadcrumb-sep">&gt;</span>
          <span>Prep sheet</span>
        </nav>
        <Flex justify="space-between" align="flex-start" wrap gap={16}>
          <div>
            <Typography.Title level={2} className="prep-sheet-page-title">
              Prep sheet
            </Typography.Title>
            <Typography.Text type="secondary">
              Per-order checklist — one card per order, hand off to the kitchen.
            </Typography.Text>
          </div>
          <Flex gap={8} wrap>
            <Button
              icon={<PrinterOutlined />}
              onClick={handlePrint}
              loading={printLoading}
              disabled={!organizationId || Boolean(filterError) || !totalCount}
            >
              Print
            </Button>
            <Button
              icon={<FilePdfOutlined />}
              type="primary"
              onClick={onExportPdf}
              loading={pdfLoading}
              disabled={!organizationId || Boolean(filterError) || !totalCount}
            >
              Export PDF
            </Button>
            <Button
              icon={<ExportOutlined />}
              loading={exporting}
              onClick={onExportCsv}
              disabled={!organizationId || Boolean(filterError)}
            >
              CSV
            </Button>
          </Flex>
        </Flex>
      </div>

      {scopedOrderId && (
        <Alert
          className="prep-sheet-no-print"
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={
            orders[0]?.order_number
              ? `Showing prep sheet for order #${orders[0].order_number}`
              : `Showing prep sheet for order #${scopedOrderId}`
          }
          action={(
            <Button
              size="small"
              onClick={() => {
                setSearchParams({});
                setDateRange([dayjs(), dayjs()]);
              }}
            >
              Show all today
            </Button>
          )}
        />
      )}

      <div className="prep-sheet-filter-bar prep-sheet-no-print">
        <div className="prep-sheet-filter-row">
          <div className="prep-sheet-filter-field">
            <span className="prep-sheet-filter-label">Date range</span>
            <RangePicker
              value={dateRange}
              disabled={Boolean(scopedOrderId)}
              onChange={(range) => setDateRange(range || [null, null])}
              style={{ width: '100%' }}
            />
          </div>
          <div className="prep-sheet-filter-field">
            <span className="prep-sheet-filter-label">Store</span>
            <Select
              mode="multiple"
              allowClear
              showSearch
              optionFilterProp="label"
              filterOption={(input, option) => (
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              )}
              placeholder="All stores"
              value={restaurantIds}
              options={restaurantOptions}
              onChange={setRestaurantIds}
              style={{ width: '100%' }}
            />
          </div>
          <div className="prep-sheet-filter-field">
            <span className="prep-sheet-filter-label">Order status</span>
            <Select
              mode="multiple"
              allowClear
              showSearch={false}
              placeholder="All statuses"
              value={statusIds}
              options={STATUS_OPTIONS}
              onChange={setStatusIds}
              style={{ width: '100%' }}
            />
          </div>
          <div className="prep-sheet-filter-field prep-sheet-filter-field--compact">
            <span className="prep-sheet-filter-label">Headcount min</span>
            <InputNumber
              min={1}
              placeholder="Min"
              value={headcountMin}
              onChange={(value) => setHeadcountMin(value == null || value < 1 ? null : value)}
              style={{ width: '100%' }}
            />
          </div>
          <div className="prep-sheet-filter-field prep-sheet-filter-field--compact">
            <span className="prep-sheet-filter-label">Headcount max</span>
            <InputNumber
              min={1}
              placeholder="Max"
              value={headcountMax}
              onChange={(value) => setHeadcountMax(value == null || value < 1 ? null : value)}
              style={{ width: '100%' }}
            />
          </div>
        </div>
        <div className="prep-sheet-matching-row">
          <div className="prep-sheet-matching">
            <span className="prep-sheet-matching-label">Matching</span>
            {prepSheetLoading ? (
              <Spin size="small" />
            ) : (
              <>
                <div className="prep-sheet-matching-count">
                  {totalCount}
                  {' '}
                  {totalCount === 1 ? 'order' : 'orders'}
                </div>
                <div className="prep-sheet-matching-sub">
                  {totalItems}
                  {' '}
                  items total
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {filterError && (
        <Alert
          type="error"
          showIcon
          message={filterError}
          className="prep-sheet-no-print"
          style={{ marginBottom: 16 }}
        />
      )}

      {prepSheetError && (
        <Alert
          type="warning"
          showIcon
          message="Unable to load prep sheets."
          className="prep-sheet-no-print"
          style={{ marginBottom: 16 }}
        />
      )}

      {prepSheetLoading ? (
        <Flex align="center" justify="center" className="h-[35vh] prep-sheet-no-print">
          <Spin size="large" />
        </Flex>
      ) : null}

      {!prepSheetLoading && orders.length === 0 ? (
        <div className="prep-sheet-no-print">
          <Empty
            description={
              scopedOrderId
                ? `No prep sheet found for order #${scopedOrderId}. It may not be a catering order in this organization, or you may not have access.`
                : 'No catering orders found for this date range. Try widening the range or choosing a different day.'
            }
          />
        </div>
      ) : null}

      {!prepSheetLoading && displayOrders.length > 0 && (
        <>
          <div className="prep-sheet-orders-list">
            {displayOrders.map((order) => {
              const ingredientIds = (order.ingredients || []).map(
                (ingredient) => ingredient.ingredient_id,
              );
              return (
                <PrepSheetOrderCard
                  key={order.order_id}
                  order={order}
                  expanded={expandedOrderIds.has(order.order_id)}
                  onToggleExpanded={() => toggleOrderExpanded(order.order_id)}
                  isChecked={(ingredientId) => isChecked(order.order_id, ingredientId)}
                  onToggleIngredient={(ingredientId, checked) => (
                    setChecked(order.order_id, ingredientId, checked)
                  )}
                  progress={getProgress(order.order_id, ingredientIds)}
                />
              );
            })}
          </div>
          {!printOrders && totalCount > PREP_SHEET_PAGE_SIZE && (
            <Flex justify="center" className="prep-sheet-pagination prep-sheet-no-print">
              <Pagination
                current={page}
                pageSize={PREP_SHEET_PAGE_SIZE}
                total={totalCount}
                showSizeChanger={false}
                onChange={setPage}
              />
            </Flex>
          )}
        </>
      )}
    </div>
  );
}

export default PrepSheet;
